import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import api from "../lib/api";
import "react-datepicker/dist/react-datepicker.css";
import {
  FaBookOpen,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaFilter,
  FaMoneyBillWave,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaTimes,
  FaWallet,
} from "react-icons/fa";
import "./CSS/CustomerCreditPremium.css";

const paymentModes = ["CASH", "UPI", "CARD", "NEFT", "RTGS"];
const creditReasons = ["Difference while reconcile", "Salary advance", "Bonus", "Adjustment", "Other"];
const tabs = [
  { id: "passbook", label: "Passbook" },
  { id: "report", label: "Reports" },
];
const pageSize = 5;

function toDateOnly(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseSafeDate(value) {
  if (!value) return null;
  const normalized = String(value).replace(" ", "T");
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTime(value) {
  const d = parseSafeDate(value) || new Date(value || Date.now());
  if (Number.isNaN(d.getTime())) return "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function money(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "warning" || notice.type === "error"
      ? FaExclamationCircle
      : FaWallet;

  return (
    <div
      className={`cc-toast ${notice.type}`}
      role={notice.type === "warning" || notice.type === "error" ? "alert" : "status"}
      aria-live={notice.type === "warning" || notice.type === "error" ? "assertive" : "polite"}
    >
      <div className="cc-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="cc-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function StatCard({ tone, icon: Icon, label, value, caption }) {
  return (
    <article className={`cc-summary-card ${tone}`}>
      <div className="cc-summary-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-summary-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <em>{caption}</em>
      </div>
    </article>
  );
}

function PassbookTransaction({ row }) {
  const isPayment = String(row.source_type || "").toUpperCase() === "PAYMENT";

  return (
    <div className={`cc-transaction ${isPayment ? "payment" : "debit"}`}>
      <div className="cc-transaction-head">
        <div>
          <strong>{row.transaction_type || "ENTRY"}</strong>
          <span>{row.description || row.comment || "-"}</span>
        </div>
        <div className="cc-transaction-meta">
          <span>{formatDateTime(row.transaction_datetime)}</span>
          {row.payment_mode ? <em>{row.payment_mode}</em> : null}
        </div>
      </div>

      <div className="cc-transaction-amounts">
        <span className={`cc-pill ${isPayment ? "credit" : "debit"}`}>
          {isPayment ? `Credit ${money(row.credit)}` : `Debit ${money(row.debit)}`}
        </span>
        <span className="cc-balance-badge">Balance {money(row.balance)}</span>
      </div>
    </div>
  );
}

export default function EmployeeCredit() {
  const token = localStorage.getItem("token");
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [employees, setEmployees] = useState([]);
  const [notice, setNotice] = useState(null);
  const [activeTab, setActiveTab] = useState("passbook");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [passbookEmployeeId, setPassbookEmployeeId] = useState("");
  const [passbookRange, setPassbookRange] = useState({
    start: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000),
    end: new Date(),
  });
  const [passbookPage, setPassbookPage] = useState(1);
  const [passbookData, setPassbookData] = useState({
    opening_balance: 0,
    transactions: [],
    summary: {
      total_debit: 0,
      total_credit: 0,
      closing_balance: 0,
    },
  });

  const [reportCredits, setReportCredits] = useState([]);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportSearch, setReportSearch] = useState("");
  const [reportEmployeeFilter, setReportEmployeeFilter] = useState("");
  const [reportStatusFilter, setReportStatusFilter] = useState("");
  const [reportPage, setReportPage] = useState(1);

  const [adjustmentForm, setAdjustmentForm] = useState({
    user_id: "",
    amount: "",
    reason: "Difference while reconcile",
    created_at: new Date(),
  });

  const [settlementForm, setSettlementForm] = useState({
    employee_id: "",
    amount_paid: "",
    payment_mode: "CASH",
    reference_no: "",
    comment: "",
    payment_date: new Date(),
  });

  const showNotice = (type, title, message) => {
    setNotice({
      id: Date.now(),
      type,
      title,
      message,
    });
  };

  const fetchEmployees = async () => {
    const res = await api.get("/api/employees/credit-passbook/employees", {
      headers: authHeaders,
    });
    setEmployees(res.data || []);
  };

  const fetchPassbook = async () => {
    if (!passbookEmployeeId) {
      setPassbookData({
        opening_balance: 0,
        transactions: [],
        summary: {
          total_debit: 0,
          total_credit: 0,
          closing_balance: 0,
        },
      });
      return;
    }

    const res = await api.get("/api/employees/credit-passbook", {
      headers: authHeaders,
      params: {
        employeeId: passbookEmployeeId,
        startDate: toDateOnly(passbookRange.start),
        endDate: toDateOnly(passbookRange.end),
      },
    });

    setPassbookData(res.data || {});
  };

  const fetchReport = async () => {
    try {
      setReportLoading(true);
      const res = await api.get("/api/employees/credit-report", {
        headers: authHeaders,
      });
      setReportCredits(res.data || []);
    } catch (err) {
      console.error("Employee credit report error:", err);
      showNotice("error", "Load failed", err?.response?.data?.error || "Unable to load employee credit report.");
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchEmployees(), fetchReport()]);
      } catch (err) {
        console.error("Employee credit bootstrap error:", err);
        showNotice("error", "Load failed", "Unable to load employee credit data.");
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(
      () => setNotice(null),
      notice.type === "warning" || notice.type === "error" ? 5200 : 3600
    );
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (employees.length && !passbookEmployeeId) {
      const firstId = String(employees[0].id);
      setPassbookEmployeeId(firstId);
      setSettlementForm((prev) => ({ ...prev, employee_id: firstId }));
      setAdjustmentForm((prev) => ({ ...prev, user_id: firstId }));
    }
  }, [employees, passbookEmployeeId]);

  useEffect(() => {
    if (!passbookEmployeeId) return undefined;
    fetchPassbook().catch((err) => {
      console.error("Employee passbook load error:", err);
      showNotice("error", "Passbook failed", err?.response?.data?.error || "Unable to load employee passbook.");
    });
    return undefined;
  }, [passbookEmployeeId, passbookRange.start, passbookRange.end]);

  useEffect(() => {
    setPassbookPage(1);
  }, [passbookEmployeeId, passbookRange.start, passbookRange.end]);

  useEffect(() => {
    setReportPage(1);
  }, [reportSearch, reportEmployeeFilter, reportStatusFilter]);

  const employeeOptions = employees.map((employee) => ({
    ...employee,
    label: employee.name || `Employee ${employee.id}`,
  }))
    .filter((employee, index, arr) => {
      const key = String(employee.id);
      return arr.findIndex((item) => String(item.id) === key) === index;
    });

  const selectedEmployee = employeeOptions.find((e) => String(e.id) === String(passbookEmployeeId));
  const passbookTransactions = passbookData.transactions || [];
  const passbookSummary = useMemo(() => {
    const totalDebit = Number(passbookData.summary?.total_debit || 0);
    const totalCredit = Number(passbookData.summary?.total_credit || 0);
    const openingBalance = Number(passbookData.opening_balance || 0);
    const closingBalance = Number(passbookData.summary?.closing_balance || 0);

    return {
      opening_balance: openingBalance,
      total_debit: totalDebit,
      total_credit: totalCredit,
      closing_balance: closingBalance,
      count: passbookTransactions.length,
    };
  }, [passbookData, passbookTransactions.length]);

  const reportSummary = useMemo(() => {
    const totalOutstanding = reportCredits.reduce((sum, row) => sum + Number(row.pending_amount || 0), 0);
    const totalGiven = reportCredits.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const totalPaid = reportCredits.reduce((sum, row) => sum + Number(row.total_paid || 0), 0);
    const paidCount = reportCredits.filter((row) => String(row.credit_status) === "paid").length;
    const employeeCount = new Set(reportCredits.map((row) => String(row.user_id))).size;

    return {
      totalOutstanding,
      totalGiven,
      totalPaid,
      paidCount,
      employeeCount,
    };
  }, [reportCredits]);

  const filteredReportRows = useMemo(() => {
    const q = reportSearch.trim().toLowerCase();
    return reportCredits.filter((row) => {
      const matchesEmployee =
        !reportEmployeeFilter || String(row.user_id) === String(reportEmployeeFilter);
      const matchesStatus =
        !reportStatusFilter || String(row.credit_status || "").toLowerCase() === reportStatusFilter;
      const haystack = [
        row.employee_name,
        row.employee_phone,
        row.credit_reason,
        row.amount,
        row.pending_amount,
        row.credit_status,
        row.latest_payment_mode,
        row.latest_reference_no,
        row.latest_payment_comment,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = !q || haystack.includes(q);
      return matchesEmployee && matchesStatus && matchesSearch;
    });
  }, [reportCredits, reportEmployeeFilter, reportSearch, reportStatusFilter]);

  const totalReportPages = Math.max(Math.ceil(filteredReportRows.length / pageSize), 1);
  const safeReportPage = Math.min(reportPage, totalReportPages);
  const reportRows = filteredReportRows.slice(
    (safeReportPage - 1) * pageSize,
    safeReportPage * pageSize
  );

  const filteredReportCount = filteredReportRows.length;

  useEffect(() => {
    setReportPage((prev) => Math.min(prev, Math.max(Math.ceil(filteredReportCount / pageSize), 1)));
  }, [filteredReportCount]);

  const passbookTotalPages = Math.max(Math.ceil(passbookTransactions.length / pageSize), 1);
  const safePassbookPage = Math.min(passbookPage, passbookTotalPages);
  const passbookRows = passbookTransactions.slice(
    (safePassbookPage - 1) * pageSize,
    safePassbookPage * pageSize
  );

  useEffect(() => {
    setPassbookPage((prev) => Math.min(prev, passbookTotalPages));
  }, [passbookTotalPages]);

  const getPageNumbers = (currentPage, totalPages) => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = start + maxButtons - 1;
    if (end > totalPages) {
      end = totalPages;
      start = end - maxButtons + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const quickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    setPassbookRange({ start, end });
  };

  const refreshAll = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchEmployees(), fetchPassbook(), fetchReport()]);
      showNotice("success", "Refreshed", "Employee credit data is up to date.");
    } catch (err) {
      console.error("Refresh error:", err);
      showNotice("error", "Refresh failed", "Unable to refresh employee credit data.");
    } finally {
      setLoading(false);
    }
  };

  const addAdjustment = async () => {
    if (!adjustmentForm.user_id) {
      showNotice("warning", "Employee required", "Please select an employee for the credit.");
      return;
    }

    if (!adjustmentForm.amount || Number(adjustmentForm.amount) <= 0) {
      showNotice("warning", "Amount required", "Please enter a valid credit amount.");
      return;
    }

    try {
      setSaving(true);
      await api.post(
        "/api/employees/createcredit",
        {
          user_id: adjustmentForm.user_id,
          amount: Number(adjustmentForm.amount),
          reason: adjustmentForm.reason,
          created_at: formatDateTime(adjustmentForm.created_at),
        },
        { headers: authHeaders }
      );

      showNotice("success", "Employee credit added", "The adjustment has been saved.");
      setAdjustmentForm({
        user_id: passbookEmployeeId || "",
        amount: "",
        reason: "Difference while reconcile",
        created_at: new Date(),
      });
      await Promise.all([fetchPassbook(), fetchReport()]);
    } catch (err) {
      console.error("Adjustment save error:", err);
      showNotice("error", "Save failed", err?.response?.data?.error || "Unable to save the employee credit.");
    } finally {
      setSaving(false);
    }
  };

  const settleEmployeeCredits = async () => {
    if (!settlementForm.employee_id) {
      showNotice("warning", "Employee required", "Please select the employee before settling.");
      return;
    }

    if (!settlementForm.amount_paid || Number(settlementForm.amount_paid) <= 0) {
      showNotice("warning", "Amount required", "Please enter a valid settlement amount.");
      return;
    }

    try {
      setSaving(true);
      await api.post(
        "/api/employees/credit-passbook/bulk-settle",
        {
          employee_id: settlementForm.employee_id,
          amount_paid: Number(settlementForm.amount_paid),
          payment_mode: settlementForm.payment_mode,
          reference_no: settlementForm.reference_no,
          comment: settlementForm.comment,
          payment_date: formatDateTime(settlementForm.payment_date),
          credit_ids: [],
        },
        { headers: authHeaders }
      );

      showNotice("success", "Payment applied", "The payment was allocated to the oldest pending employee credits.");
      setSettlementForm((prev) => ({
        ...prev,
        amount_paid: "",
        reference_no: "",
        comment: "",
        payment_date: new Date(),
      }));
      await Promise.all([fetchPassbook(), fetchReport()]);
    } catch (err) {
      console.error("Employee settlement error:", err);
      showNotice("error", "Settlement failed", err?.response?.data?.error || "Unable to settle the employee credits.");
    } finally {
      setSaving(false);
    }
  };

  const selectEmployee = (employeeId) => {
    const value = String(employeeId || "");
    setPassbookEmployeeId(value);
    setSettlementForm((prev) => ({ ...prev, employee_id: value }));
    setAdjustmentForm((prev) => ({ ...prev, user_id: value }));
  };

  const openEmployeePassbook = (employeeId) => {
    selectEmployee(employeeId);
    setActiveTab("passbook");
  };

  return (
    <div className="cc-page text-light">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />

      <section className="cc-hero">
        <div>
          <div className="cc-eyebrow">Employee Finance</div>
          <h2>Employee credit, passbook first with a proper report.</h2>
          <p>
            Settle employee credits in bulk, keep the running ledger in passbook order, and use the
            report tab when you need the detailed line items for audit.
          </p>
          <div className="cc-hero-meta">
            <span>{employees.length} employees</span>
            <span>{money(passbookSummary.closing_balance)} closing balance</span>
            <span>{reportSummary.paidCount} settled rows</span>
          </div>
        </div>

        <div className="cc-hero-actions">
          <button type="button" className="btn cc-secondary-action" onClick={refreshAll} disabled={loading || reportLoading}>
            <FaRedoAlt aria-hidden="true" />
            Refresh
          </button>
          <button type="button" className="btn cc-primary-action" onClick={() => setActiveTab(activeTab === "passbook" ? "report" : "passbook")}>
            {activeTab === "passbook" ? "Go to reports" : "Back to passbook"}
          </button>
        </div>
      </section>

      <section className="cc-context-strip">
        <div className="cc-context-head">
          <div>
            <span>Ledger context</span>
            <h3>Choose employee and date range first</h3>
          </div>
          <div className="cc-context-chip">
            <strong>{selectedEmployee?.label || "Selected employee"}</strong>
            <span>{money(passbookSummary.closing_balance)} closing balance</span>
          </div>
        </div>

        <div className="cc-context-grid">
          <div>
            <label className="form-label">Employee</label>
            <select
              className="form-select"
              value={passbookEmployeeId}
              onChange={(e) => selectEmployee(e.target.value)}
            >
              <option value="">Select employee</option>
              {employeeOptions.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">Start date</label>
            <DatePicker
              className="form-control"
              selected={passbookRange.start}
              onChange={(d) => setPassbookRange({ ...passbookRange, start: d || new Date() })}
              dateFormat="yyyy-MM-dd"
              popperClassName="cc-date-popper"
            />
          </div>

          <div>
            <label className="form-label">End date</label>
            <DatePicker
              className="form-control"
              selected={passbookRange.end}
              onChange={(d) => setPassbookRange({ ...passbookRange, end: d || new Date() })}
              dateFormat="yyyy-MM-dd"
              popperClassName="cc-date-popper"
            />
          </div>
        </div>

        <div className="cc-passbook-toolbar">
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(1)}>
            1D
          </button>
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(7)}>
            1W
          </button>
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(14)}>
            2W
          </button>
          <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => quickRange(30)}>
            30D
          </button>
        </div>
      </section>

      <div className="cc-tabs" role="tablist" aria-label="Employee credit views">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`cc-tab-btn ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "passbook" ? (
        <>
          <section className="cc-summary-grid">
            <StatCard
              tone="blue"
              icon={FaMoneyBillWave}
              label="Opening balance"
              value={money(passbookSummary.opening_balance)}
              caption="Balance before selected range"
            />
            <StatCard
              tone="green"
              icon={FaWallet}
              label="Credit total"
              value={money(passbookSummary.total_debit)}
              caption="Employee credits added"
            />
            <StatCard
              tone="amber"
              icon={FaCheckCircle}
              label="Payments"
              value={money(passbookSummary.total_credit)}
              caption="Bulk settlements applied"
            />
            <StatCard
              tone="slate"
              icon={FaClock}
              label="Closing balance"
              value={money(passbookSummary.closing_balance)}
              caption={`${passbookSummary.count} passbook entries`}
            />
          </section>

          <section className="cc-grid">
            <div className="cc-card cc-form-card">
              <div className="cc-card-head">
                <div>
                  <span>Bulk settlement</span>
                  <h3>Apply one payment across employee credit rows</h3>
                </div>
                <div className="cc-ledger-chip">
                  <strong>FIFO</strong>
                  <span>Oldest pending first</span>
                </div>
              </div>

              <div className="cc-form-grid">
                <div>
                  <label className="form-label">Employee</label>
                  <select
                    className="form-select"
                    value={settlementForm.employee_id}
                    onChange={(e) => selectEmployee(e.target.value)}
                  >
                    <option value="">Select employee</option>
                    {employeeOptions.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Payment date</label>
                  <DatePicker
                    className="form-control"
                    selected={settlementForm.payment_date}
                    onChange={(d) => setSettlementForm({ ...settlementForm, payment_date: d || new Date() })}
                    showTimeSelect
                    dateFormat="yyyy-MM-dd HH:mm"
                    popperPlacement="bottom-start"
                  />
                </div>

                <div>
                  <label className="form-label">Amount</label>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Settlement amount"
                    value={settlementForm.amount_paid}
                    onChange={(e) => setSettlementForm({ ...settlementForm, amount_paid: e.target.value })}
                  />
                </div>

                <div>
                  <label className="form-label">Payment mode</label>
                  <select
                    className="form-select"
                    value={settlementForm.payment_mode}
                    onChange={(e) => setSettlementForm({ ...settlementForm, payment_mode: e.target.value })}
                  >
                    {paymentModes.map((mode) => (
                      <option key={mode} value={mode}>
                        {mode}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Reference no</label>
                  <input
                    className="form-control"
                    placeholder="UPI, cheque, bank ref"
                    value={settlementForm.reference_no}
                    onChange={(e) => setSettlementForm({ ...settlementForm, reference_no: e.target.value })}
                  />
                </div>

                <div>
                  <label className="form-label">Comment</label>
                  <input
                    className="form-control"
                    placeholder="Optional note"
                    value={settlementForm.comment}
                    onChange={(e) => setSettlementForm({ ...settlementForm, comment: e.target.value })}
                  />
                </div>
              </div>

              <div className="cc-action-row">
                <button type="button" className="btn cc-primary-action" onClick={settleEmployeeCredits} disabled={saving}>
                  <FaSave aria-hidden="true" />
                  {saving ? "Settling..." : "Apply payment"}
                </button>
                <div className="cc-inline-note">
                  Payments are allocated to the oldest pending employee credits.
                </div>
              </div>
            </div>

            <div className="cc-card cc-form-card">
              <div className="cc-card-head">
                <div>
                  <span>Employee credit</span>
                  <h3>Add a manual credit or adjustment</h3>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-light cc-mini-btn"
                  onClick={() =>
                    setAdjustmentForm({
                      user_id: passbookEmployeeId || "",
                      amount: "",
                      reason: "Difference while reconcile",
                      created_at: new Date(),
                    })
                  }
                >
                  Reset
                </button>
              </div>

              <div className="cc-form-grid">
                <div>
                  <label className="form-label">Employee</label>
                  <select
                    className="form-select"
                    value={adjustmentForm.user_id}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, user_id: e.target.value })}
                  >
                    <option value="">Select employee</option>
                    {employeeOptions.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Created at</label>
                  <DatePicker
                    className="form-control"
                    selected={adjustmentForm.created_at}
                    onChange={(d) => setAdjustmentForm({ ...adjustmentForm, created_at: d || new Date() })}
                    showTimeSelect
                    dateFormat="yyyy-MM-dd HH:mm"
                    popperPlacement="bottom-start"
                  />
                </div>

                <div>
                  <label className="form-label">Amount</label>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Credit amount"
                    value={adjustmentForm.amount}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: e.target.value })}
                  />
                </div>

                <div>
                  <label className="form-label">Reason</label>
                  <select
                    className="form-select"
                    value={adjustmentForm.reason}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                  >
                    {creditReasons.map((reason) => (
                      <option key={reason} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="cc-action-row">
                <button type="button" className="btn cc-primary-action" onClick={addAdjustment} disabled={saving}>
                  <FaPlus aria-hidden="true" />
                  {saving ? "Saving..." : "Add credit"}
                </button>
              </div>
            </div>
          </section>

          <section className="cc-card cc-timeline-card">
            <div className="cc-card-head">
              <div>
                <span>Passbook</span>
                <h3>Running ledger for {selectedEmployee?.label || "selected employee"}</h3>
              </div>
            </div>

            <div className="cc-timeline">
              {passbookRows.length ? (
                passbookRows.map((row) => (
                  <PassbookTransaction key={`${row.source_type}-${row.source_id}`} row={row} />
                ))
              ) : (
                <div className="cc-empty-state">
                  <FaBookOpen aria-hidden="true" />
                  <strong>No passbook activity in this range</strong>
                  <span>Choose another employee or date range to see the ledger flow.</span>
                </div>
              )}
            </div>

            {passbookTransactions.length > pageSize && (
              <div className="cc-pagination">
                <button
                  type="button"
                  className="cc-page-btn"
                  disabled={safePassbookPage === 1}
                  onClick={() => setPassbookPage((p) => p - 1)}
                >
                  Prev
                </button>

                <div className="cc-page-list">
                  {getPageNumbers(safePassbookPage, passbookTotalPages).map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`cc-page-btn ${safePassbookPage === page ? "active" : ""}`}
                      onClick={() => setPassbookPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="cc-page-btn"
                  disabled={safePassbookPage === passbookTotalPages}
                  onClick={() => setPassbookPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </>
      ) : (
        <section className="cc-card cc-report-card">
          <div className="cc-summary-grid">
            <StatCard
              tone="blue"
              icon={FaMoneyBillWave}
              label="Total credit"
              value={money(reportSummary.totalGiven)}
              caption="All employee credit rows"
            />
            <StatCard
              tone="green"
              icon={FaWallet}
              label="Outstanding"
              value={money(reportSummary.totalOutstanding)}
              caption="Still pending"
            />
            <StatCard
              tone="amber"
              icon={FaCheckCircle}
              label="Settled"
              value={money(reportSummary.totalPaid)}
              caption="Paid against credits"
            />
            <StatCard
              tone="slate"
              icon={FaClock}
              label="Paid rows"
              value={String(reportSummary.paidCount)}
              caption={`${reportSummary.employeeCount} employees in report`}
            />
          </div>

          <div className="cc-card-head">
            <div>
              <span>Report lines</span>
              <h3>Detailed employee credit rows for audit and export</h3>
            </div>
            <div className="cc-table-meta">
              <span>{filteredReportCount} rows</span>
              <strong>{safeReportPage}/{totalReportPages}</strong>
            </div>
          </div>

          <div className="cc-filter-row">
            <div className="cc-search-shell">
              <FaSearch aria-hidden="true" />
              <input
                className="form-control"
                placeholder="Search employee, reason, ref, mode"
                value={reportSearch}
                onChange={(e) => setReportSearch(e.target.value)}
              />
            </div>

            <div className="cc-filter-group">
              <select
                className="form-select"
                value={reportEmployeeFilter}
                onChange={(e) => setReportEmployeeFilter(e.target.value)}
              >
                <option value="">All employees</option>
                {employeeOptions.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.label}
                  </option>
                ))}
              </select>

              <select
                className="form-select"
                value={reportStatusFilter}
                onChange={(e) => setReportStatusFilter(e.target.value)}
              >
                <option value="">All status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div>

          <div className="table-responsive cc-table-wrap">
            <table className="table cc-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Amount</th>
                  <th>Pending</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Latest payment</th>
                  <th>Reason</th>
                  <th style={{ width: 150 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {reportRows.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Employee">
                      <strong>{row.employee_name || "-"}</strong>
                      <span>{row.employee_phone || "Employee credit"}</span>
                    </td>
                    <td data-label="Amount">{money(row.amount)}</td>
                    <td data-label="Pending">{money(row.pending_amount)}</td>
                    <td data-label="Status">
                      <span className={`cc-pill ${String(row.credit_status || "").toLowerCase()}`}>
                        {row.credit_status || "pending"}
                      </span>
                    </td>
                    <td data-label="Date">{formatDateTime(row.credit_created_at)}</td>
                    <td data-label="Latest payment">
                      <div className="cc-mini-stack">
                        <strong>
                          {row.latest_payment_date ? `${row.latest_payment_mode || "PAYMENT"}` : "No payment yet"}
                        </strong>
                        <span>
                          {row.latest_payment_date
                            ? `${money(row.latest_amount_paid || row.total_paid || 0)}`
                            : "Waiting on settlement"}
                        </span>
                      </div>
                    </td>
                    <td data-label="Reason">{row.credit_reason || row.latest_payment_comment || "-"}</td>
                    <td data-label="Action" className="cc-row-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-success"
                        onClick={() => openEmployeePassbook(row.user_id)}
                      >
                        Open passbook
                      </button>
                    </td>
                  </tr>
                ))}

                {!reportRows.length && (
                  <tr>
                    <td colSpan="8">
                      <div className="cc-empty-state">
                        <FaFilter aria-hidden="true" />
                        <strong>No employee credit rows found</strong>
                        <span>Try changing the employee, status, or search filter.</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredReportCount > pageSize && (
            <div className="cc-pagination">
              <button
                type="button"
                className="cc-page-btn"
                disabled={safeReportPage === 1}
                onClick={() => setReportPage((p) => p - 1)}
              >
                Prev
              </button>

              <div className="cc-page-list">
                {getPageNumbers(safeReportPage, totalReportPages).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`cc-page-btn ${safeReportPage === page ? "active" : ""}`}
                    onClick={() => setReportPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="cc-page-btn"
                disabled={safeReportPage === totalReportPages}
                onClick={() => setReportPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
